import {
    formatDate,
    GetProviderMessageRequest,
    ListProviderMessagesOrderEnum,
    ListProviderMessagesRequest,
    MeInfoResponse,
    MessageAuthorType,
    MessageCommentAuthor,
    MessageLinkEntityType,
    ProviderComment,
    ProviderDisplayDocumentType,
    ProviderDocument,
    ProviderDocumentUrl,
    ProviderMessage,
    ProviderMessageSortType,
} from '@enter/core';
import * as d3 from 'd3';
import { faker } from '@faker-js/faker';
import _ from 'lodash';
import { ClaimGenerator } from './claim.generator';
import { ICommentCompose, IMessageCompose, IMessageDetail, IMessageListing } from './message.store';
import { RootStore } from './root.store';
import { waitForNoReason } from './util';

interface IScriptedComment {
    author: MessageCommentAuthor;
    subject?: string;
    body: string;
    filename?: string;
}

const fmtDate = formatDate('DateTime');
export class MessageGenerator {
    public messages: ProviderMessage[] = [];
    public comments: ProviderComment[] = [];
    public documents: ProviderDocument[] = [];
    public documentUrls: ProviderDocumentUrl[] = [];

    private claimGenerator: ClaimGenerator;
    private rootStore: RootStore;
    private authors: MessageCommentAuthor[] = [];
    private index: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

    constructor(claimGenerator: ClaimGenerator, rootStore: RootStore) {
        this.claimGenerator = claimGenerator;
        this.rootStore = rootStore;

        this.generateEntities(100);
        this.generatePrefabricated();
    }

    public async loadMessageCount(request: ListProviderMessagesRequest): Promise<number> {
        const unread = _.filter(this.messages, {
            read: request.filterRead === 'EQ:0',
            open: request.filterOpen === 'EQ:1',
        });
        await waitForNoReason(400);
        return _.size(unread);
    }

    public async loadMessageListing(request: ListProviderMessagesRequest): Promise<IMessageListing> {
        await waitForNoReason(400); // add some lag so loader appears
        // base case uses filter to split Inbox messages from Done messages
        const matching: ProviderMessage[] = _.filter<ProviderMessage>(
            this.messages,
            (message: ProviderMessage): boolean => {
                const { filterOpen, filterRead } = request;
                const matchesOpen = filterOpen ? message.open === (filterOpen === 'EQ:1') : true;
                const matchesRead = filterRead ? message.read === (filterRead === 'EQ:1') : true;
                return matchesOpen && matchesRead;
            },
        );
        const sorted = this.sortMessages(matching, request.sort, request.order);

        // paginate results
        const offset = request.offset || 0; // default to 0
        const limit = request.limit || _.size(this.messages) - offset; // default to (total - offset)
        const paginated = _.slice(sorted, offset, offset + limit);
        return {
            messages: paginated,
            total: _.size(matching),
        };
    }

    public async loadMessage(request: GetProviderMessageRequest): Promise<ProviderMessage> {
        await waitForNoReason(400); // add some lag so loader appears
        return this.findOrCreateMessage(request.id);
    }

    public async loadMessageDetail(messageId: string): Promise<IMessageDetail> {
        const message = this.findOrCreateMessage(messageId);
        const comments: ProviderComment[] | undefined = this.loadFromIndex('comments', messageId);
        const documents: ProviderDocument[] | undefined = this.loadFromIndex('documents', messageId);
        const documentUrls: ProviderDocumentUrl[] | undefined = this.loadFromIndex('documentUrls', messageId, 'url');
        const detail = {
            message,
            comments,
            documents,
            documentUrls,
        };
        await waitForNoReason(400); // add some lag so loader appears
        return detail;
    }

    public loadDocumentUrl = (documentId: string): ProviderDocumentUrl | undefined => {
        const documentUrls: ProviderDocumentUrl[] | undefined = this.loadFromIndex('documentUrls', documentId, 'url');
        let documentUrl = _.find(documentUrls, { documentId });
        if (!documentUrl) {
            documentUrl = this.generateProviderDocumentUrl(documentId);
        }
        return documentUrl;
    };

    public loadDocument = (id: string): ProviderDocument | undefined => {
        const documents: ProviderDocument[] | undefined = this.loadFromIndex('documents', id, 'id');
        let document = _.find(documents, { id });
        if (!document) {
            document = this.generateProviderDocument(id);
        }
        return document;
    };

    public composeMessage(messageCompose: IMessageCompose, account?: MeInfoResponse): IMessageDetail {
        const message = this.generateProviderMessage({ date: new Date() });
        if (!message || !message.id) {
            throw new Error('Failed to generate message');
        }
        if (account) {
            message.author = this.generateMessageCommentAuthor(account);
        }

        // NB: overwrite some fields of generated message
        _.assign(message, messageCompose);
        const comments: ProviderComment[] | undefined = this.loadFromIndex('comments', message.id);
        const documents: ProviderDocument[] | undefined = this.loadFromIndex('documents', message.id);

        return {
            message,
            comments,
            documents,
        };
    }

    public composeComment(commentCompose: ICommentCompose, account?: MeInfoResponse): ProviderComment {
        const { messageId, body, files } = commentCompose;

        // create documents
        const [documents] = _.map(files, file => {
            const document = {
                id: faker.string.uuid(),
                filename: file.name,
                filesize: file.size,
            };
            const documentUrl = {
                documentId: document.id,
                url: faker.image.url(),
            };
            this.storeToIndex('documents', messageId, document);
            this.storeToIndex('documentUrls', messageId, documentUrl, 'url');
            return [document, documentUrl];
        });

        // create comment
        const comment: ProviderComment = {
            id: faker.string.uuid(),
            body: body || ' ',
            documents: _.map(documents, 'id'),
            author: this.generateMessageCommentAuthor(account),
            created: fmtDate(new Date()),
        };
        this.storeToIndex('comments', messageId, comment);

        return comment;
    }

    public markAsOpen = (id: string): void => {
        const message = _.find(this.messages, { id });
        if (message) {
            message.open = true;
        }
    };

    public markAsClosed = (id: string): void => {
        const message = _.find(this.messages, { id });
        if (message) {
            message.open = false;
        }
    };

    public markAsRead = (id: string): void => {
        const message = _.find(this.messages, { id });
        if (message) {
            message.read = true;
        }
    };

    public embed = (id: string): ProviderDocumentUrl => {
        // TODO: Replace url to real fake url
        return { documentId: id, url: '' };
    };

    private findOrCreateMessage = (messageId: string): ProviderMessage => {
        let message = _.find(this.messages, { id: messageId });
        if (!message) {
            message = this.generateProviderMessage({ messageId });
        }
        return message;
    };

    private generateEntities(count: number): void {
        // generate a fixed pool of 'from' names
        this.authors = _.times(4, () => ({
            id: faker.string.uuid(),
            nickname: this.generateName(),
            type: _.sample([
                MessageAuthorType.PROVIDER,
                MessageAuthorType.SUPPORT,
                MessageAuthorType.SUPPORT,
                MessageAuthorType.SUPPORT,
                MessageAuthorType.SUPPORT,
                MessageAuthorType.SUPPORT,
            ]), // NB: prefer more support than provider
        }));

        // generate a fixed pool of messages with variable dates
        const yesterday = new Date(new Date().getTime() - 24 * 3600 * 1000);
        _.times(count, (i: number) => {
            const date = this.recentDateBefore(yesterday, i + 1);
            this.generateProviderMessage({ date });
        });
    }

    private generateScripts(support: MessageCommentAuthor, provider: MessageCommentAuthor): IScriptedComment[][] {
        return [
            [
                {
                    author: provider,
                    subject: 'Incorrect billed amount',
                    body:
                        'I think there is something wrong with this claim for John Smith. The billed amount seems low.',
                },
                {
                    author: support,
                    body:
                        'Hello! Thank you for bringing this to our attention. Let us investigate and get right back to you.',
                },
                {
                    author: provider,
                    body: 'OK. Thank you.',
                },
                {
                    author: support,
                    body:
                        'Hi there. You are correct. The amount billed for the patient visit was actually $190, not $100. We have just retransmitted the corrected claim to the payer.',
                },
                {
                    author: provider,
                    body: 'Thanks so much. :D',
                },
            ],
            [
                {
                    author: support,
                    subject: 'Missing Demographic info',
                    body:
                        'Hi there! This claim is missing demographic information. Please submit it when you have a moment.',
                },
                {
                    author: provider,
                    body: 'Hi! Please see the attached Member ID',
                    filename: 'Member_ID.pdf',
                },
                {
                    author: support,
                    body: "Thank you very much. Can you please provide the Patient's mobile phone number?",
                },
                {
                    author: provider,
                    body: 'Here you go: (702) 555-2019',
                },
                {
                    author: support,
                    body: 'Thank you very much! The claim was submitted!',
                },
            ],
        ];
    }

    // TODO: consider dynamic claim lookup to replace 'John Smith' with real name
    // TODO: add documents
    private generatePrefabricated(): void {
        const support = {
            id: faker.string.uuid(),
            nickname: 'Daenarys Targaryen',
            type: MessageAuthorType.SUPPORT,
        };
        const provider = {
            id: faker.string.uuid(),
            nickname: this.generateName(),
            type: MessageAuthorType.PROVIDER,
        };
        const scripts = this.generateScripts(support, provider);

        // add messages/comments for each script
        _.forEach(scripts, script => {
            // update message: root author, subject
            let dt: Date = faker.date.recent();
            // generate message
            const m1 = this.generateProviderMessage({
                date: dt,
                author: _.first(_.compact(_.map(script, 'author'))),
                subject: _.first(_.compact(_.map(script, 'subject'))),
                comments: [],
            });
            const messageId = m1.id as string;
            // generate comments
            const comments = _.map(script, line => {
                dt = this.recentDateAfter(dt);
                const documents = [];
                if (line.filename) {
                    documents.push(this.generateAttachment(messageId, line.filename)[0]);
                }
                const comment: ProviderComment = {
                    id: faker.string.uuid(),
                    body: line.body,
                    author: line.author,
                    documents: _.compact(_.map(documents, 'id')),
                    created: fmtDate(dt),
                };
                this.storeToIndex('comments', messageId, comment);
                return comment;
            });
            m1.comments = comments;
            m1.commentCount = comments.length;
            m1.open = true;
            m1.read = false;
        });
    }

    private generateProviderMessage({
        date = faker.date.recent(),
        messageId = faker.string.uuid(),
        author = _.sample(this.authors) as MessageCommentAuthor,
        subject = faker.lorem.sentence(),
        comments,
    }: {
        date?: Date;
        messageId?: string;
        author?: MessageCommentAuthor;
        subject?: string;
        comments?: ProviderComment[];
    }): ProviderMessage {
        const claim = _.sample(this.claimGenerator.claims);
        if (!comments) {
            comments = _.orderBy(
                _.concat(
                    [],
                    this.generateProviderComment(messageId, date, author),
                    _.times(_.random(0, 4), () => this.generateProviderComment(messageId, date)),
                ),
                'created',
                'desc',
            );
        }
        const lastAuthor = _.get(_.last(comments), 'author');
        const message: ProviderMessage = {
            id: messageId,
            author,
            lastAuthor,
            subject,
            comments: comments,
            commentCount: comments.length,
            documentCount: _.get(comments, '[0].documents.length', 0), // only count documents for root comment
            open: faker.datatype.boolean(),
            read: faker.datatype.boolean(),
            links: {
                claims: [{
                    id: claim?.id as string,
                    entityType: MessageLinkEntityType.CLAIM,
                }],
            },
            updated: fmtDate(date),
            created: fmtDate(date),
        };
        this.messages.push(message);
        return message;
    }

    private generateProviderComment(
        messageId: string,
        date: Date,
        author: MessageCommentAuthor = _.sample(this.authors) as MessageCommentAuthor,
        body: string = faker.lorem.sentence(),
        filename?: string,
    ): ProviderComment {
        const [documents] = _.times(_.random(0, 1), () => this.generateAttachment(messageId, filename));
        const comment: ProviderComment = {
            id: faker.string.uuid(),
            body,
            documents: _.map(documents, 'id'),
            author,
            created: fmtDate(faker.date.between({ from: date, to: new Date() })),
        };
        this.storeToIndex('comments', messageId, comment);
        return comment;
    }

    public generateProviderDocument(filename: string = faker.system.commonFileName('pdf')): ProviderDocument {
        return {
            id: faker.string.uuid(),
            filename,
            filesize: _.random(1e3, 1e8),
            documentType: ProviderDisplayDocumentType.GUARANTORBILL,
            description: 'Test document',
            thumbnail: 'https://via.placeholder.com/150',
        };
    }

    private generateProviderDocumentUrl(documentId: string): ProviderDocumentUrl {
        return {
            documentId,
            url: faker.image.url(),
        };
    }

    private generateAttachment(messageId: string, filename?: string): [ProviderDocument, ProviderDocumentUrl] {
        const document = this.generateProviderDocument(filename);
        const documentId = document.id as string; // just created it...
        const documentUrl = this.generateProviderDocumentUrl(documentId);
        this.storeToIndex('documents', messageId, document);
        this.storeToIndex('documentUrls', documentId, documentUrl, 'url');
        return [document, documentUrl];
    }

    private generateMessageCommentAuthor(account?: MeInfoResponse): MessageCommentAuthor {
        return {
            id: _.get(account, 'userd', faker.string.uuid()),
            nickname: _.get(account, 'personalInfo.name', this.generateName()),
            type: MessageAuthorType.PROVIDER,
        };
    }

    private generateName(): string {
        return `${faker.person.firstName()} ${faker.person.lastName()}`;
    }

    private recentDateAfter(startDate: Date, range: number = 7): Date {
        const endDate = d3.utcDay.offset(startDate, _.random(1, range));
        return faker.date.between({ from: startDate, to: endDate });
    }

    private recentDateBefore(endDate: Date, range: number = 7): Date {
        const startDate = d3.utcDay.offset(endDate, -1 * _.random(1, range));
        return faker.date.between({ from: startDate, to: endDate });
    }

    private storeToIndex<T>(model: string, key: string, values: T | T[], field: string = 'id'): void {
        if (!this.index.has(model)) {
            this.index.set(model, new Map<string, string[]>());
        }
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const items = _.concat([], values);
            const oldIds = index.get(key) || [];
            const newIds = _.map(items, field);
            index.set(key, [...oldIds, ...newIds]);
            items.forEach(item => array.indexOf(item) === -1 && array.push(item));
        }
    }
    private loadFromIndex<T>(model: string, key: string, field: string = 'id'): T[] | undefined {
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const ids = index.get(key);
            return _.filter(array, (item: T): boolean => _.includes(ids, _.get(item, field)));
        }
    }

    private sortMessages(
        messages: ProviderMessage[],
        sorts: ProviderMessageSortType[] = [],
        orders: ListProviderMessagesOrderEnum[] = [],
    ): ProviderMessage[] {
        // sort results
        let sortCriteria = _.map(sorts, (sort: ProviderMessageSortType) => _.toLower(sort));
        if (_.isEmpty(sortCriteria)) {
            // NB: expect both ProviderMessage and SupportMessage to have 'updated' prop
            sortCriteria.push('updated');
        }
        // NB: extra magic for sorting by author
        sortCriteria = _.map(sortCriteria, sort => (sort === 'author' ? 'author' : sort));

        // NB: expect both provider/support enums to have 'ASCENDING'
        const orderCriteria = _.map(orders, order => (String(order) === 'ASCENDING' ? 'asc' : 'desc'));
        if (_.isEmpty(orderCriteria)) {
            orderCriteria.push('desc');
        }
        return _.orderBy(messages, sortCriteria, orderCriteria);
    }
}
